import { useEffect, useState } from "react";
import { api } from "../api/client";
import { useRole } from "../context/RoleContext";
import { useT } from "../i18n";
import { formatDate } from "../utils/format";
import { ConfirmModal } from "./ConfirmModal";
import { MeetingsPanel } from "./ProcessMeetingsPanel";
import { IMPACT_KEYS, canReviewImpact } from "../../shared/workflow";

export function ProcessWorkPanel({
  detail,
  reload,
  onDirtyChange,
}: {
  detail: any;
  reload: () => Promise<any>;
  onDirtyChange: (dirty: boolean) => void;
}) {
  const { t, lang } = useT();
  const { isCde, role } = useRole();
  const [tab, setTab] = useState(() =>
    new URLSearchParams(window.location.hash.split("?")[1]).get("tab") ===
    "meetings"
      ? "meetings"
      : "work",
  );
  const [people, setPeople] = useState<any[]>([]),
    [index, setIndex] = useState<any[]>([]),
    [history, setHistory] = useState<any[]>([]),
    [reviews, setReviews] = useState<any[]>([]);
  const [form, setForm] = useState<any>({}),
    [saved, setSaved] = useState(""),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false),
    [message, setMessage] = useState("");
  const [pauseReason, setPauseReason] = useState(""),
    [mode, setMode] = useState(detail.workflow_mode),
    [modeReason, setModeReason] = useState(""),
    [publishOpen, setPublishOpen] = useState(false),
    [note, setNote] = useState("");
  const editable = isCde && ["active", "on_hold"].includes(detail.state);
  const dirty = !!saved && JSON.stringify(form) !== saved;
  const loadExtras = () =>
    Promise.all([
      api.getWorkHistory(detail.id).then(setHistory),
      api.getWorkReviews(detail.id).then(setReviews),
    ]);
  useEffect(() => {
    const f = {
      client: detail.client || "",
      consultant_person_id: detail.consultant_person_id || null,
      git_tier: detail.git_tier || "",
      git_area: detail.git_area || "",
      goal: detail.goal || "",
      next_action: detail.next_action || "",
      target_version: detail.target_version || "",
      revision_review: detail.revision_review || "",
      revision_impacts: detail.revision_impacts || [],
    };
    setForm(f);
    setSaved(JSON.stringify(f));
    setMode(detail.workflow_mode);
    loadExtras().catch((e) => setError(e.message));
  }, [detail.id, detail.updated_at]);
  useEffect(() => {
    api
      .getPeople()
      .then(setPeople)
      .catch(() => {});
    api
      .getRepoIndex()
      .then((i) => setIndex(i.entries))
      .catch(() => {});
  }, []);
  useEffect(() => {
    onDirtyChange(dirty);
    return () => onDirtyChange(false);
  }, [dirty]);
  const act = async (fn: () => Promise<any>) => {
    setBusy(true);
    setError("");
    setMessage("");
    try {
      await fn();
      await reload();
      await loadExtras();
      setMessage(t("control.saved"));
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  const save = () => act(() => api.updateProcess(detail.id, form));
  const impactLabel = (key: string) =>
    t(`control.${key === "client" ? "clientImpact" : key}`);
  const domains = [
    ...new Set(index.map((e) => `${e.tier}/${e.areaSegments.join("/")}`)),
  ].sort();
  const statusControl = editable &&
    detail.state === "active" &&
    detail.work_status !== "done" && (
      <div className="flex gap-8 mt-12">
        {detail.work_status !== "in_progress" && (
          <button
            className="btn btn-accent btn-sm"
            disabled={busy || dirty}
            onClick={() =>
              act(() =>
                api.moveWork(
                  detail.id,
                  detail.work_status === "inbox" ? "todo" : "in_progress",
                ),
              )
            }
          >
            {t("control.move")}{" "}
            {t(
              detail.work_status === "inbox"
                ? "control.todo"
                : "control.in_progress",
            )}
          </button>
        )}
      </div>
    );
  return (
    <div className="control-work-panel">
      <div className="tabs" role="tablist" aria-label={t("control.work")}>
        {["work", "meetings", "notes", "history"].map((key) => (
          <button
            className={`tab control-view-tab${tab === key ? " active" : ""}`}
            key={key}
            role="tab"
            aria-selected={tab === key}
            onClick={() => setTab(key)}
          >
            {t(`control.${key}`)}
          </button>
        ))}
      </div>
      {error && (
        <p className="text-err text-sm" role="alert">
          {error}
        </p>
      )}
      {message && (
        <p className="text-ok text-sm" role="status">
          {message}
        </p>
      )}
      {tab === "work" && (
        <>
          {isCde && detail.state === "live" && (
            <button
              className="btn btn-accent btn-sm mb-12"
              disabled={busy}
              onClick={() =>
                act(async () => {
                  const revision = await api.createProcess({
                    name: detail.name,
                    kind: "rework",
                    reworks_process_id: detail.id,
                  });
                  window.location.hash = `#/process/${revision.id}`;
                })
              }
            >
              {t("fiche.p8.openRework")}
            </button>
          )}
          <div className="flex gap-8 mb-12 control-wrap">
            <span className="badge badge-accent">
              {t(`control.${detail.work_status}`)}
            </span>
            <span className="badge">
              {t(
                detail.kind === "rework"
                  ? "control.revision"
                  : "control.creation",
              )}
            </span>
          </div>
          {detail.published_version ? (
            <p className="text-sm">
              {t("control.published")} :{" "}
              <a
                className="text-accent"
                href={`#/process/${detail.published_process_id}`}
              >
                {detail.published_version}
              </a>
            </p>
          ) : (
            <p className="text-xs text-ink-3">{t("control.noPublished")}</p>
          )}
          {detail.state === "on_hold" && (
            <p className="text-warn text-sm">
              {t("control.paused")} : {detail.hold_reason}
            </p>
          )}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              save();
            }}
          >
            <fieldset disabled={!editable || busy} className="control-fieldset">
              <label className="form-group">
                <span className="form-label">{t("control.client")}</span>
                <input
                  className="form-input"
                  value={form.client ?? ""}
                  onChange={(e) => setForm({ ...form, client: e.target.value })}
                />
              </label>
              <label className="form-group">
                <span className="form-label">{t("control.consultant")}</span>
                <select
                  className="form-select"
                  value={form.consultant_person_id || ""}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      consultant_person_id: Number(e.target.value) || null,
                    })
                  }
                >
                  <option value="">{t("control.unassigned")}</option>
                  {people.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
                {detail.consultant_name?.includes(' / ') && <span className="text-xs text-ink-3">{t('rep.sharedAssignment',{names:detail.consultant_name})}</span>}
              </label>
              <label className="form-group">
                <span className="form-label">{t("control.domain")}</span>
                <select
                  className="form-select"
                  value={`${form.git_tier}/${form.git_area}`}
                  onChange={(e) => {
                    const [tier, ...area] = e.target.value.split("/");
                    setForm({
                      ...form,
                      git_tier: tier,
                      git_area: area.join("/"),
                    });
                  }}
                >
                  <option value={`${form.git_tier}/${form.git_area}`}>
                    {[form.git_tier, form.git_area]
                      .filter(Boolean)
                      .join(" › ") || t("control.noDomain")}
                  </option>
                  {domains
                    .filter((d) => d !== `${form.git_tier}/${form.git_area}`)
                    .map((d) => (
                      <option key={d} value={d}>
                        {d.replaceAll("/", " › ")}
                      </option>
                    ))}
                </select>
              </label>
              <label className="form-group">
                <span className="form-label">{t("fiche.p1.goal")}</span>
                <textarea
                  className="form-textarea"
                  rows={2}
                  value={form.goal ?? ""}
                  onChange={(e) => setForm({ ...form, goal: e.target.value })}
                />
              </label>
              <label className="form-group">
                <span className="form-label">{t("control.nextAction")}</span>
                <textarea
                  className="form-textarea"
                  rows={3}
                  value={form.next_action ?? ""}
                  onChange={(e) =>
                    setForm({ ...form, next_action: e.target.value })
                  }
                />
              </label>
              <label className="form-group">
                <span className="form-label">{t("control.targetVersion")}</span>
                <input
                  className="form-input"
                  placeholder="v2"
                  value={form.target_version ?? ""}
                  onChange={(e) =>
                    setForm({ ...form, target_version: e.target.value })
                  }
                />
              </label>
              {detail.workflow_mode === "revision" && (
                <>
                  <div className="form-group">
                    <span className="form-label">{t("control.impacts")}</span>
                    <div className="control-impact-options">
                      {IMPACT_KEYS.map((key) => (
                        <label key={key} className="text-sm">
                          <input
                            type="checkbox"
                            checked={(form.revision_impacts ?? []).includes(
                              key,
                            )}
                            onChange={(e) =>
                              setForm({
                                ...form,
                                revision_impacts: e.target.checked
                                  ? [...form.revision_impacts, key]
                                  : form.revision_impacts.filter(
                                      (k: string) => k !== key,
                                    ),
                              })
                            }
                          />{" "}
                          {impactLabel(key)}
                        </label>
                      ))}
                    </div>
                  </div>
                  <label className="form-group">
                    <span className="form-label">{t("control.review")}</span>
                    <textarea
                      className="form-textarea"
                      rows={4}
                      placeholder={t("control.reviewHint")}
                      value={form.revision_review ?? ""}
                      onChange={(e) =>
                        setForm({ ...form, revision_review: e.target.value })
                      }
                    />
                  </label>
                </>
              )}
            </fieldset>
            {editable && (
              <button
                className="btn btn-accent btn-sm"
                disabled={busy || !dirty}
              >
                {t("common.save")}
              </button>
            )}
          </form>
          {detail.work_status === "inbox" && (
            <p className="text-xs text-ink-3 mt-12">
              {t("control.qualificationHint")}
            </p>
          )}
          {statusControl}
          {detail.workflow_mode === "revision" && (
            <div className="mt-16">
              <p className="text-xs text-ink-3">{t("control.reviewOwner")}</p>
              {(detail.revision_impacts ?? []).map((key: string) => {
                const review = reviews.find(
                  (r) => r.impact === key && r.approved_by,
                );
                return (
                  <div className="control-review" key={key}>
                    <span>{impactLabel(key)}</span>
                    {review ? (
                      <span className="text-xs text-ok">
                        {t("control.reviewed", {
                          name: review.approved_by_name,
                        })}
                      </span>
                    ) : canReviewImpact(role, key as any) &&
                      detail.state === "active" ? (
                      <button
                        className="btn btn-sm"
                        disabled={busy || dirty}
                        onClick={() =>
                          act(() => api.approveRevision(detail.id, key))
                        }
                      >
                        {t("control.approve")}
                      </button>
                    ) : (
                      <span className="text-xs text-ink-3">
                        {t("control.reviewPending")}
                      </span>
                    )}
                  </div>
                );
              })}
              {editable && detail.work_status === "in_progress" && (
                <button
                  className="btn btn-accent mt-12"
                  disabled={busy || dirty}
                  onClick={() => setPublishOpen(true)}
                >
                  {t("control.publish")}
                </button>
              )}
            </div>
          )}
          {editable && (
            <details className="mt-16">
              <summary className="text-sm">
                {detail.state === "on_hold"
                  ? t("control.resume")
                  : t("control.pause")}
              </summary>
              {detail.state !== "on_hold" && (
                <label className="form-group mt-12">
                  <span className="form-label">{t("control.pauseReason")}</span>
                  <input
                    className="form-input"
                    value={pauseReason}
                    onChange={(e) => setPauseReason(e.target.value)}
                  />
                </label>
              )}
              <button
                className="btn btn-sm mt-12"
                disabled={
                  busy ||
                  dirty ||
                  (detail.state !== "on_hold" && !pauseReason.trim())
                }
                onClick={() =>
                  act(() =>
                    api.holdWork(
                      detail.id,
                      detail.state !== "on_hold",
                      pauseReason,
                    ),
                  )
                }
              >
                {t(
                  detail.state === "on_hold"
                    ? "control.resume"
                    : "control.pause",
                )}
              </button>
            </details>
          )}
          {editable &&
            detail.kind === "rework" &&
            detail.phase_key === "intake" && (
              <details className="mt-16">
                <summary className="text-sm">{t("control.mode")}</summary>
                <select
                  className="form-select mt-12"
                  aria-label={t("control.mode")}
                  value={mode}
                  onChange={(e) => setMode(e.target.value)}
                >
                  <option value="revision">{t("control.lightRevision")}</option>
                  <option value="full">{t("control.fullCycle")}</option>
                </select>
                <label className="form-group mt-12">
                  <span className="form-label">{t("control.modeReason")}</span>
                  <input
                    className="form-input"
                    value={modeReason}
                    onChange={(e) => setModeReason(e.target.value)}
                  />
                </label>
                <button
                  className="btn btn-sm"
                  disabled={
                    busy ||
                    dirty ||
                    mode === detail.workflow_mode ||
                    !modeReason.trim()
                  }
                  onClick={() =>
                    act(() => api.setWorkMode(detail.id, mode, modeReason))
                  }
                >
                  {t("control.changeMode")}
                </button>
              </details>
            )}
        </>
      )}
      {tab === "notes" && (
        <>
          <div className="flex flex-col gap-12">
            {detail.notes.map((n: any) => (
              <div className="seance-card" key={n.id}>
                <span className="text-xs text-ink-3">
                  {formatDate(n.seance_date || n.created_at, lang)}
                </span>
                <p className="control-note-body">{n.body}</p>
                {n.transcript && (
                  <details>
                    <summary>{t("control.transcript")}</summary>
                    <p className="control-note-body">{n.transcript}</p>
                  </details>
                )}
              </div>
            ))}
          </div>
          {!detail.notes.length && (
            <p className="text-sm text-ink-3">{t("control.emptyNotes")}</p>
          )}
          {isCde && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                act(async () => {
                  await api.addSeance(detail.id, { body: note });
                  setNote("");
                });
              }}
            >
              <label className="form-group">
                <span className="form-label">{t("control.noteBody")}</span>
                <textarea
                  className="form-textarea"
                  rows={4}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
              </label>
              <button className="btn btn-sm" disabled={busy || !note.trim()}>
                {t("control.addNote")}
              </button>
            </form>
          )}
        </>
      )}
      {tab === "meetings" && (
        <MeetingsPanel
          processId={detail.id}
          code={detail.registry_code}
          history={history}
        />
      )}
      {tab === "history" && (
        <>
          <h3 className="section-label mb-12">{t("control.history")}</h3>
          {history.map((h) => (
            <a
              className="card control-history-entry"
              href={`#/process/${h.id}`}
              key={h.id}
            >
              <strong>{h.target_version || h.name}</strong>
              <span className="text-xs text-ink-3">
                {t(
                  h.kind === "rework" ? "control.revision" : "control.creation",
                )}{" "}
                ·{" "}
                {t(
                  h.state === "superseded"
                    ? "control.superseded"
                    : h.state === "cancelled"
                      ? "control.cancelled"
                      : `control.${h.work_status}`,
                )}{" "}
                · {formatDate(h.completed_at || h.created_at, lang)}
              </span>
            </a>
          ))}
          <h3 className="section-label mb-12 mt-16">{t("control.notes")}</h3>
          {detail.events.map((e: any) => (
            <div key={e.id} className="control-history-event">
              <span>{e.actor_name}</span>
              <span className="text-xs text-ink-3">
                {formatDate(e.created_at, lang)}
              </span>
              <div className="text-xs">
                {e.event_type === "work_updated"
                  ? Object.entries(e.details?.changes ?? {})
                      .map(
                        ([k, v]: any) =>
                          `${t(`control.${k === "next_action" ? "nextAction" : k === "target_version" ? "targetVersion" : k}`)} : ${typeof v.after === "string" ? v.after : JSON.stringify(v.after)}`,
                      )
                      .join(" · ")
                  : e.details?.reason || e.details?.version || e.event_type}
              </div>
            </div>
          ))}
        </>
      )}
      <ConfirmModal
        open={publishOpen}
        title={t("control.publish")}
        message={t("control.publishConfirm")}
        confirmLabel={t("control.publish")}
        onCancel={() => setPublishOpen(false)}
        onConfirm={() => {
          setPublishOpen(false);
          act(() => api.publishRevision(detail.id));
        }}
      />
    </div>
  );
}
